import React, { useEffect, useRef, useState } from "react";
import { Input, Radio, Button as AntButton, Popover } from "antd";
import type { RadioChangeEvent } from "antd/es/radio";
import { SearchOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";

import filterIcon from "../../assets/images/icon-filter.svg";
import { formatTranslation } from "../../shared/utils/translationUtils";

const radioGroupStyle: React.CSSProperties = {
  display: "flex",
  flexDirection: "column",
  gap: 8,
};

interface ProtocolFilterPopoverContentProps {
  status: string;
  onStatusChange: (value: string) => void;
  onReset: () => void;
  onApply: () => void;
  activeTab: string;
}

const ProtocolFilterPopoverContent = ({
  status,
  onStatusChange,
  onReset,
  onApply,
  activeTab,
}: ProtocolFilterPopoverContentProps): JSX.Element => {
  const { t } = useTranslation();
  let filterOptions = [
    { label: formatTranslation(t("protocolFilter.allStatuses")), value: "all" },
    { label: formatTranslation(t("protocolFilter.draft")), value: "draft" },
    {
      label: formatTranslation(t("protocolFilter.published")),
      value: "published",
    },
  ];

  if (activeTab === "templates") {
    filterOptions = [
      { label: formatTranslation(t("templateFilter.allTypes")), value: "all" },
      { label: formatTranslation(t("templateFilter.seed")), value: "seed" },
      {
        label: formatTranslation(t("templateFilter.biologicals")),
        value: "biological",
      },
      {
        label: formatTranslation(t("templateFilter.nutrients")),
        value: "nutrient",
      },
    ];
  }

  return (
    <div style={{ padding: 12, minWidth: 200 }}>
      <Radio.Group
        onChange={(event: RadioChangeEvent) => {
          onStatusChange(event.target.value);
        }}
        style={radioGroupStyle}
        value={status}
      >
        {filterOptions.map((option) => (
          <Radio key={option.value} value={option.value}>
            {option.label}
          </Radio>
        ))}
      </Radio.Group>
      <div style={{ margin: "12px 0" }} />
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <AntButton type="link" onClick={onReset}>
          {formatTranslation(t("protocolFilter.clear"))}
        </AntButton>
        <AntButton type="primary" onClick={onApply}>
          {formatTranslation(t("protocolFilter.apply"))}
        </AntButton>
      </div>
    </div>
  );
};

interface ProtocolsHeaderActionsProps {
  onSearch: (value: string) => void;
  onFilter: (status: string) => void;
  searchValue: string;
  activeTab: string;
}

export const ProtocolsHeaderActions = ({
  onSearch,
  onFilter,
  searchValue,
  activeTab,
}: ProtocolsHeaderActionsProps): JSX.Element => {
  const { t } = useTranslation();
  const [searchText, setSearchText] = useState<string>(searchValue);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [status, setStatus] = useState<string>("all");
  const typingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    setSearchText(searchValue);
  }, [searchValue]);

  useEffect(() => {
    return () => {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
    };
  }, []);

  const applyFilter = (): void => {
    onFilter(status === "all" ? "" : status);
    setDropdownVisible(false);
  };

  const resetFilter = (): void => {
    setStatus("all");
    onFilter("");
    setDropdownVisible(false);
  };

  const handleSearchChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ): void => {
    const value = event.target.value;
    setSearchText(value);

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      onSearch(value);
    }, 1500);
  };

  return (
    <div className="header-actions">
      <Input
        placeholder={formatTranslation(
          t("configure.search.button.placeholder")
        )}
        allowClear
        value={searchText}
        onChange={handleSearchChange}
        style={{ width: 200, marginRight: "16px" }}
        prefix={<SearchOutlined style={{ color: "#696f88" }} />}
      />
      {activeTab !== "archived" && (
        <Popover
          content={
            <ProtocolFilterPopoverContent
              status={status}
              onStatusChange={setStatus}
              onReset={resetFilter}
              onApply={applyFilter}
              activeTab={activeTab}
            />
          }
          trigger="click"
          open={dropdownVisible}
          onOpenChange={setDropdownVisible}
          placement="bottomRight"
          classNames={{ root: "protocol-filter-popover" }}
        >
          <AntButton icon={<img src={filterIcon} alt="Filter" />} />
        </Popover>
      )}
    </div>
  );
};
